import React, { Component } from 'react';
import Media from 'react-media';
import { func, object, array, string, bool } from 'prop-types';
import { NavLink, Link } from 'react-router-dom';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import Fade from '@material-ui/core/Fade';
/* eslint-disable import/no-unassigned-import */
import 'whatwg-fetch';
/* eslint-enable import/no-unassigned-import */

import { logout } from '../util/user';
import { isPremium, examDiffProUrl } from '../util/common';
import diffnowLogo from '../diffnow.png';

// The comparison types, shown as the main navigation in the header
const tools = [
  { tool: 'compare-clips', label: 'Text' },
  { tool: 'compare-files', label: 'Files' },
  { tool: 'compare-urls', label: 'URLs' },
  { tool: 'compare-folders', label: 'Folders (ZIP)' },
  { tool: 'samples', label: 'Samples' }
];

const barFont = {
  fontFamily: 'Roboto, "Segoe UI", Arial, sans-serif',
  fontWeight: 500,
  fontSize: 16
};

class TitleBar extends Component {
  static propTypes = {
    user: object,
    optionsProfiles: array,
    activeTool: string,
    setCurrentTab: func,
    onUserLogout: func,
    toggleNightMode: func,
    openLoginDialog: func,
    openSavedReportsDialog: func,
    openWhyPremiumDialog: func,
    openChangePasswordDialog: func,
    openManagePremiumSubscriptionDialog: func,
    openAboutDialog: func,
    openChangeLogDialog: func,
    openContactDialog: func,
    openTermsOfServiceDialog: func,
    nightModeEnabled: bool
  };

  state = {
    helpAnchorEl: null,
    userAnchorEl: null
  };

  get isNight() {
    return !!this.props.nightModeEnabled;
  }

  selectTool = tool => {
    this.props.setCurrentTab(tool);
    window.localStorage.setItem('diffNowLastViewedTab', `/${tool}`);
  };

  // Flat header button; Get Premium is the only filled one
  barButton(label, onClick, extra = {}) {
    const { style, ...props } = extra;
    return (
      <Button
        onClick={onClick}
        style={{
          ...barFont,
          color: 'white',
          textTransform: 'none',
          minHeight: 40,
          minWidth: 0,
          padding: '8px 10px',
          borderRadius: 6,
          whiteSpace: 'nowrap',
          ...style
        }}
        {...props}
      >
        {label}
      </Button>
    );
  }

  menuItem(label, onClick, close) {
    return (
      <MenuItem
        onClick={() => {
          close();
          onClick();
        }}
      >
        {label}
      </MenuItem>
    );
  }

  navigation(matches) {
    return (
      <nav
        aria-label="Comparison type"
        style={{
          display: 'flex',
          gap: 4,
          overflowX: 'auto',
          order: matches.small ? 3 : 0,
          width: matches.small ? '100%' : 'auto',
          paddingBottom: matches.small ? 6 : 0
        }}
      >
        {tools.map(({ tool, label }) => {
          const active = tool === this.props.activeTool;
          return (
            <Link
              key={tool}
              id={`${tool}-tab`}
              to={`/${tool}`}
              onClick={() => this.selectTool(tool)}
              aria-current={active ? 'page' : undefined}
              style={{
                ...barFont,
                color: active ? (this.isNight ? '#1E4F8F' : '#0D47A1') : 'white',
                backgroundColor: active ? 'white' : 'transparent',
                textDecoration: 'none',
                padding: '8px 14px',
                borderRadius: 6,
                whiteSpace: 'nowrap'
              }}
            >
              {label}
            </Link>
          );
        })}
      </nav>
    );
  }

  helpMenu() {
    const close = () => this.setState({ helpAnchorEl: null });
    return (
      <div>
        {this.barButton(
          <React.Fragment>
            Help
            <Icon style={{ fontSize: 20 }}>expand_more</Icon>
          </React.Fragment>,
          e => this.setState({ helpAnchorEl: e.currentTarget }),
          {
            'aria-owns': this.state.helpAnchorEl ? 'help-menu' : null,
            'aria-haspopup': 'true',
            style: { paddingRight: 6 }
          }
        )}
        <Menu
          id="help-menu"
          open={!!this.state.helpAnchorEl}
          anchorEl={this.state.helpAnchorEl}
          onClose={close}
          TransitionComponent={Fade}
        >
          {this.menuItem('About DiffNow', this.props.openAboutDialog, close)}
          {this.menuItem("What's new", this.props.openChangeLogDialog, close)}
          {!isPremium(this.props.user) &&
            this.menuItem(
              'Why Premium?',
              () => this.props.openWhyPremiumDialog('HELP_MENU'),
              close
            )}
          {this.menuItem('Contact us', this.props.openContactDialog, close)}
          {this.menuItem('Terms of Service', this.props.openTermsOfServiceDialog, close)}
        </Menu>
      </div>
    );
  }

  nightModeButton() {
    return this.barButton(
      <Icon style={{ fontSize: 22 }}>{this.isNight ? 'wb_sunny' : 'brightness_2'}</Icon>,
      this.props.toggleNightMode,
      { 'aria-label': 'Switch night mode', title: 'Switch night mode' }
    );
  }

  // Logged in: one account button whose menu holds the account actions
  accountMenu(matches) {
    const { user } = this.props;
    const close = () => this.setState({ userAnchorEl: null });
    return (
      <div>
        {this.barButton(
          <React.Fragment>
            <Icon style={{ fontSize: 22, marginRight: matches.small ? 0 : 6 }}>account_circle</Icon>
            {!matches.small && (
              <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email}
              </span>
            )}
            <Icon style={{ fontSize: 20 }}>expand_more</Icon>
          </React.Fragment>,
          e => this.setState({ userAnchorEl: e.currentTarget }),
          {
            'aria-owns': this.state.userAnchorEl ? 'user-menu' : null,
            'aria-haspopup': 'true',
            title: user.email,
            style: { paddingRight: 6 }
          }
        )}
        <Menu
          id="user-menu"
          open={!!this.state.userAnchorEl}
          anchorEl={this.state.userAnchorEl}
          onClose={close}
          TransitionComponent={Fade}
        >
          {this.menuItem('Saved reports', this.props.openSavedReportsDialog, close)}
          {this.menuItem('Change password', this.props.openChangePasswordDialog, close)}
          {user.isPremium &&
            this.menuItem(
              'Manage Premium subscription',
              this.props.openManagePremiumSubscriptionDialog,
              close
            )}
          {this.menuItem(
            'Log out',
            () => logout(user.sessionToken, this.props.onUserLogout),
            close
          )}
        </Menu>
      </div>
    );
  }

  title() {
    return (
      <NavLink exact to="/" style={{ height: 56, display: 'flex', alignItems: 'center' }}>
        <img src={diffnowLogo} style={{ height: 34, aspectRatio: '249 / 57' }} alt="DiffNow" />
      </NavLink>
    );
  }

  premiumButton() {
    return isPremium(this.props.user)
      ? null
      : this.barButton('Get Premium', () => this.props.openWhyPremiumDialog('TITLE_BAR'), {
          style: { backgroundColor: '#FFEE58', color: '#222222', padding: '8px 14px' }
        });
  }

  render() {
    return (
      <Media queries={{ small: '(max-width: 599px)' }}>
        {matches => (
          <AppBar
            position="static"
            style={{ backgroundColor: this.isNight ? '#1E4F8F' : '#1565C0', boxShadow: 'none' }}
          >
            <Toolbar
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 16,
                minHeight: 56,
                paddingLeft: 16,
                paddingRight: 16
              }}
            >
              {this.title()}
              {this.navigation(matches)}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                {!matches.small &&
                  this.barButton('Desktop app', undefined, {
                    href: examDiffProUrl('diffnow-header'),
                    target: '_blank',
                    rel: 'noopener noreferrer'
                  })}
                {this.helpMenu()}
                {this.nightModeButton()}
                {this.props.user
                  ? this.accountMenu(matches)
                  : this.barButton('Log in', this.props.openLoginDialog)}
                {this.premiumButton()}
              </div>
            </Toolbar>
          </AppBar>
        )}
      </Media>
    );
  }
}

export default TitleBar;
